import { isPlainObject, omit, set } from 'lodash'

import { ANIMATION_KEYS } from '@/components/builder/properties/Animation/animationsPredefined'
import { SHARED_KEYS, SHARED_PATTERN_DEFS } from '@/components/builder/utils'
import { cloneNodeTree, parseSerializedTree } from '@/components/builder/utils/serialize-block'

import { buildDocument } from './document'
import { buildOutline } from './outline'
import { describePath, ensureMobileLayoutType, escapeContent, expandProps, isIgnoreDeviceProp, isLayoutPath, isTextProp, mergeBlockProps, normalizeValue, validateProps, validateValue, writeProp } from './props'
import { createError, DEVICES, ERRORS, FINAL_OPS, HISTORY_OPS, LIMITS, MUTATING_OPS, OPS, RESTRICTED_BLOCKS, STATE_INCLUDES, localCommandId, serializeError } from './protocol'
import { createRefResolver } from './refs'
import { createRevisionLog } from './revision'
import { sleep, withTimeout } from './timers'

const FRAME_FALLBACK_MS = 50

const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden'

const settled = store => new Promise(resolve => {
  let quiet = null
  let unsubscribe = () => {}

  const done = () => {
    clearTimeout(quiet)
    clearTimeout(deadline)
    unsubscribe()
    resolve()
  }

  const deadline = setTimeout(done, LIMITS.settleAfterMutation)
  const restart = () => {
    clearTimeout(quiet)
    quiet = setTimeout(done, LIMITS.settleQuiet)
  }

  unsubscribe = store.subscribe(state => state.nodes, restart)
  restart()
})

const nextFrame = () => new Promise(resolve => {
  const hidden = isHidden()
  const timer = setTimeout(resolve, hidden ? 0 : FRAME_FALLBACK_MS)

  if (!hidden && typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      clearTimeout(timer)
      resolve()
    })
  }
})

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const LINKED_RETRY_STEP = 50

const isLinkedNotReady = error => error?.code === ERRORS.NODE_NOT_FOUND && Array.isArray(error.details?.linkedNodes)

async function runWithLinkedRetry(attempt) {
  const deadline = Date.now() + LIMITS.linkedNodeTimeout

  for (;;) {
    try {
      return await attempt()
    } catch (error) {
      if (!isLinkedNotReady(error)) {
        throw error
      }
      if (Date.now() >= deadline) {
        throw createError(ERRORS.LINKED_NODE_NOT_READY, `${error.message} (its linked nodes were not created within ${LIMITS.linkedNodeTimeout}ms)`, error.details)
      }
      await sleep(LINKED_RETRY_STEP)
    }
  }
}

const clampIndex = (index, length) => {
  if (index === undefined || index === null) {
    return length
  }

  const number = Number(index)
  if (!Number.isInteger(number)) {
    throw createError(ERRORS.INVALID_OP, 'index must be an integer')
  }

  return clamp(number, 0, length)
}

const measure = value => JSON.stringify(value).length

function truncateState(result, maxChars) {
  const output = { ...result }
  if (measure(output) <= maxChars) {
    return output
  }

  const truncated = []

  if (output.css) {
    truncated.push('css')
    output.css = { truncated: true, chars: output.css.length }
  }

  if (measure(output) > maxChars && output.html) {
    truncated.push('html')
    output.html = { truncated: true, chars: output.html.length }
  }

  if (measure(output) > maxChars && output.theme) {
    truncated.push('theme')
    output.theme = { truncated: true, chars: measure(output.theme) }
  }

  if (measure(output) > maxChars && output.patterns) {
    truncated.push('patterns')
    output.patterns = { truncated: true, keys: Object.keys(output.patterns) }
  }

  if (measure(output) > maxChars && output.document) {
    const nodes = [...output.document.nodes]
    while (nodes.length && measure({ ...output, document: { ...output.document, nodes } }) > maxChars) {
      nodes.splice(Math.floor(nodes.length / 2))
    }
    truncated.push('document')
    output.document = { ...output.document, nodes, truncated: true, kept: nodes.length }
  }

  output.truncated = truncated
  return output
}

const BATCH_NAME = /^[A-Za-z0-9_-]{1,40}$/

function validateOps(ops, { batch, batchIndex, single, last }) {
  if (!Array.isArray(ops) || !ops.length) {
    throw createError(ERRORS.INVALID_BATCH, `Batch "${batch}" needs a non-empty ops array`, { batchIndex, batch })
  }

  ops.forEach((op, index) => {
    if (!isPlainObject(op) || !OPS.includes(op.op)) {
      throw createError(ERRORS.INVALID_OP, `Unknown op at index ${index} of batch "${batch}"`, { batchIndex, batch, opIndex: index, ops: OPS })
    }

    if (HISTORY_OPS.includes(op.op) && !(single && ops.length === 1)) {
      throw createError(ERRORS.INVALID_BATCH, `"${op.op}" must be the only op of the command`, { batchIndex, batch, opIndex: index })
    }

    if (FINAL_OPS.includes(op.op) && !(last && index === ops.length - 1)) {
      throw createError(ERRORS.INVALID_BATCH, `"${op.op}" must be the last op of the last batch`, { batchIndex, batch, opIndex: index })
    }
  })
}

function toBatches(command) {
  if (Array.isArray(command.batches)) {
    return command.batches
  }

  return [{ name: 'batch', ops: command.ops }]
}

function validateBatches(batches) {
  if (!Array.isArray(batches) || !batches.length) {
    throw createError(ERRORS.INVALID_BATCH, 'batches must be a non-empty array')
  }

  const names = new Set()

  batches.forEach((batch, index) => {
    if (!isPlainObject(batch) || typeof batch.name !== 'string' || !BATCH_NAME.test(batch.name)) {
      throw createError(ERRORS.INVALID_BATCH, `Batch ${index} needs a name of letters, digits, _ or -`, { batchIndex: index })
    }

    if (names.has(batch.name)) {
      throw createError(ERRORS.INVALID_BATCH, `Batch name "${batch.name}" is used twice`, { batchIndex: index, batch: batch.name })
    }

    for (const dependency of batch.dependsOn || []) {
      if (!names.has(dependency)) {
        throw createError(ERRORS.INVALID_BATCH, `Batch "${batch.name}" depends on "${dependency}", which is not an earlier batch`, { batchIndex: index, batch: batch.name, dependsOn: dependency })
      }
    }

    names.add(batch.name)
    validateOps(batch.ops, { batch: batch.name, batchIndex: index, single: batches.length === 1, last: index === batches.length - 1 })
  })
}

const aliasOf = ref => {
  if (typeof ref !== 'string' || !ref.startsWith('$')) {
    return null
  }
  return ref.slice(1).split(/[./]/)[0] || null
}

const usedAliases = ops => {
  const used = new Set()
  for (const op of ops) {
    for (const ref of [op.node, op.parent, op.match?.within]) {
      const alias = aliasOf(ref)
      if (alias) {
        used.add(alias)
      }
    }
  }
  return used
}

const createdAliases = ops => ops.map(op => op.as).filter(Boolean).map(name => name.replace(/^\$/, ''))

const batchDependencies = batches => {
  const producers = new Map()

  return batches.map(batch => {
    const dependencies = new Set(batch.dependsOn || [])
    for (const alias of usedAliases(batch.ops)) {
      const producer = producers.get(alias)
      if (producer && producer !== batch.name) {
        dependencies.add(producer)
      }
    }
    for (const alias of createdAliases(batch.ops)) {
      producers.set(alias, batch.name)
    }
    return [...dependencies]
  })
}

export function createRuntime({ store, resolver, blocks, helpers = {} }) {
  const { actions, query } = store
  const aliases = new Map()
  const blocksByKey = blocks.reduce((map, block) => ({ ...map, [block.key]: block }), {})
  const refs = createRefResolver({ query, aliases })
  const revision = createRevisionLog({ store })
  let focus = null

  revision.start()

  const nodeData = id => query.getNodes()[id]?.data
  const definitionOf = key => blocksByKey[key]?.definition
  const paletteKeys = () => blocks
    .filter(block => block.palette && !RESTRICTED_BLOCKS.includes(block.key))
    .map(block => block.key)

  const requireBlock = key => {
    const block = blocksByKey[key]

    if (!block || !resolver[key]) {
      throw createError(ERRORS.UNKNOWN_BLOCK, `Unknown block "${key}"`, { block: key, known: paletteKeys() })
    }

    if (RESTRICTED_BLOCKS.includes(key)) {
      throw createError(ERRORS.BLOCK_NOT_ALLOWED, `Block "${key}" cannot be added by the agent`, { block: key })
    }

    return block
  }

  const requireAllowed = id => {
    const block = nodeData(id)?.name
    if (RESTRICTED_BLOCKS.includes(block)) {
      throw createError(ERRORS.BLOCK_NOT_ALLOWED, `Block "${block}" cannot be edited by the agent`, { id, block })
    }
  }

  const requireCanvas = parentId => {
    if (query.node(parentId).isCanvas()) {
      return
    }

    const node = nodeData(parentId)
    const linkedNodes = Object.keys(node.linkedNodes || {})

    throw createError(ERRORS.PARENT_NOT_CANVAS, `"${refs.pathOf(parentId)}" does not accept children directly`, {
      id: parentId,
      block: node.name,
      linkedNodes,
      hint: linkedNodes.length ? `Use one of its linked nodes, for example "${parentId}/${linkedNodes[0]}"` : 'Pick a canvas node as the parent',
    })
  }

  const alias = (name, id) => {
    if (name) {
      aliases.set(String(name).replace(/^\$/, ''), id)
    }
  }

  const focusRoot = () => {
    if (!focus) {
      return 'ROOT'
    }

    try {
      return refs.resolve(focus)
    } catch (_error) {
      focus = null
      return 'ROOT'
    }
  }

  const resolveRoot = root => root ? refs.resolve(root) : focusRoot()
  const currentDocument = root => buildDocument(query.getSerializedNodes(), { root })

  const parentIndex = id => {
    const parentId = nodeData(id)?.parent
    return parentId ? nodeData(parentId).nodes.indexOf(id) : -1
  }

  const freshNode = (block, properties, custom) => query.parseFreshNode({
    data: {
      type: block.Component,
      props: properties ? { properties } : {},
      custom,
    },
  }).toNode()

  const commonAncestor = ids => {
    const chains = ids.map(id => {
      const chain = [id]
      let current = nodeData(id)?.parent
      while (current) {
        chain.unshift(current)
        current = nodeData(current)?.parent
      }
      return chain
    })

    let ancestor = 'ROOT'
    for (let depth = 0; depth < chains[0].length; depth++) {
      const candidate = chains[0][depth]
      if (!chains.every(chain => chain[depth] === candidate)) {
        break
      }
      ancestor = candidate
    }

    return ancestor
  }

  const imageEntries = (block, path, value) => {
    if (block === 'image' && path === 'attributes' && value?.src) {
      return [{ src: value.src, name: value.name }]
    }

    if (block === 'image' && path === 'attributes.src') {
      return [{ src: value }]
    }

    if (path === 'background.image' && value?.src) {
      return [{ src: value.src, name: value.name }]
    }

    if (path === 'background.image.src') {
      return [{ src: value }]
    }

    return []
  }

  const registerImages = (ctx, entries) => {
    const fresh = entries.filter(entry => typeof entry.src === 'string' && /^https?:\/\//.test(entry.src))
    if (!fresh.length) {
      return
    }

    ctx.run().setCustom('ROOT', custom => {
      const images = Array.isArray(custom.images) ? custom.images : []
      fresh.forEach(({ src, name }) => {
        if (!images.some(image => image?.src === src)) {
          images.push({ src, name: name || src.split('/').pop().split('?')[0] })
        }
      })
      custom.images = images
    })
    ctx.sync()
    ctx.touch('ROOT')
  }

  const rootCustom = () => nodeData('ROOT')?.custom || {}

  const patternSelector = key => {
    const prefix = SHARED_PATTERN_DEFS[key]?.opts?.prefix
    return `.${prefix === undefined ? 'pe-builder-' : prefix || ''}${key}`
  }

  const prepareWrites = (definition, block, props, { device, merge }) => expandProps(definition, props, { leaves: merge }).map(([path, raw]) => {
    const value = normalizeValue(path, raw)
    validateValue(definition, path, value, block)

    return {
      path,
      value: isTextProp(block, path) && typeof value === 'string' ? escapeContent(value) : value,
      ignoreDevice: isIgnoreDeviceProp(definition, path, block),
      fallbackDesktop: device === 'mobile' ? describePath(definition, path)?.defaultValue : undefined,
    }
  })

  const applyWrites = (target, writes, { device, state, merge }, warn) => {
    for (const { path, value, ignoreDevice, fallbackDesktop } of writes) {
      const warning = writeProp(target, path, value, { device, state, ignoreDevice, merge, fallbackDesktop })
      if (warning) {
        warn(path, warning)
      }

      if (device === 'mobile' && isLayoutPath(path)) {
        ensureMobileLayoutType(target, path)
      }
    }
  }

  const stringLeaves = value => {
    if (typeof value === 'string') {
      return [value]
    }
    return isPlainObject(value) ? Object.values(value).flatMap(stringLeaves) : []
  }

  const knownKeyframes = () => {
    const custom = (rootCustom().keyframe || []).map(item => item?.name).filter(Boolean)
    return [...new Set([...Object.keys(ANIMATION_KEYS), ...custom])]
  }

  const requireKeyframes = writes => {
    const names = writes
      .filter(({ path }) => path === 'animation')
      .flatMap(({ value }) => (Array.isArray(value) ? value : []).map(item => item?.name).filter(Boolean))
    const known = knownKeyframes()
    const unknown = names.filter(name => !known.includes(name))

    if (unknown.length) {
      throw createError(ERRORS.INVALID_VALUE, `Unknown animation${unknown.length > 1 ? 's' : ''}: ${unknown.join(', ')}`, {
        path: 'animation.name',
        value: unknown,
        values: known,
      })
    }

    return names
  }

  const registerKeyframes = (ctx, names) => {
    const current = rootCustom().tailwind?.keyframe
    const registered = Array.isArray(current) ? current : Object.keys(current || {})
    const missing = [...new Set(names)].filter(name => !registered.includes(name))

    if (!missing.length) {
      return
    }

    ctx.run().setCustom('ROOT', custom => {
      const existing = custom.tailwind?.keyframe
      const next = Array.isArray(existing) ? [...existing, ...missing] : [...Object.keys(existing || {}), ...missing]
      set(custom, ['tailwind', 'keyframe'], next)
    })
    ctx.sync()
  }

  const registerFonts = async (ctx, writes) => {
    if (!helpers.resolveFontUrl) {
      return
    }

    const fonts = [...new Set(writes.filter(({ path }) => path === 'fontFamily').flatMap(({ value }) => stringLeaves(value)))]
    const configured = rootCustom().tailwind?.font || {}

    for (const font of fonts.filter(name => !configured[name])) {
      const url = await helpers.resolveFontUrl(font)
      if (url) {
        ctx.run().setCustom('ROOT', custom => {
          set(custom, ['tailwind', 'font', font], url)
        })
        ctx.sync()
      }
    }
  }

  const subtreeBlocks = id => {
    const names = []
    const walk = nodeId => {
      const data = nodeData(nodeId)
      if (!data) {
        return
      }
      names.push(data.name)
      ;[...data.nodes || [], ...Object.values(data.linkedNodes || {})].forEach(walk)
    }
    walk(id)
    return names
  }

  const touchedOutline = touched => {
    const ids = [...touched].filter(id => id && refs.exists(id))
    if (!ids.length) {
      return undefined
    }

    return buildOutline(currentDocument(), {
      root: commonAncestor(ids),
      depth: LIMITS.touchedOutlineDepth,
      maxNodes: LIMITS.touchedOutlineMaxNodes,
    })
  }

  const requireDevice = device => {
    if (!DEVICES.includes(device)) {
      throw createError(ERRORS.INVALID_OP, `Unknown device "${device}"`, { devices: DEVICES })
    }
  }

  const stageWrites = (definition, block, props, { device, merge }) => {
    validateProps(definition, props, block)
    const writes = prepareWrites(definition, block, props, { device, merge })
    return { writes, keyframes: requireKeyframes(writes) }
  }

  const registerSideEffects = async (ctx, { writes, keyframes }, images = []) => {
    registerImages(ctx, images)
    registerKeyframes(ctx, keyframes)
    await registerFonts(ctx, writes)
  }

  const screenshotTarget = (node, touched) => {
    if (node) {
      return refs.resolve(node)
    }

    const ids = [...touched || []].filter(id => refs.exists(id))
    return ids.length ? commonAncestor(ids) : focusRoot()
  }

  const screenshotScale = scale => {
    if (scale === undefined || scale === null) {
      return undefined
    }

    const [min, max] = LIMITS.screenshotScale
    const number = Number(scale)
    if (!Number.isFinite(number) || number < min || number > max) {
      throw createError(ERRORS.INVALID_OP, `scale must be a number between ${min} and ${max}`, { scale })
    }

    return number
  }

  const takeScreenshot = async (op = {}, touched) => {
    if (!helpers.screenshot) {
      throw createError(ERRORS.INTERNAL, 'Screenshots are not available here')
    }

    const scale = screenshotScale(op.scale)
    const nodeId = screenshotTarget(op.node, touched)
    const shot = await withTimeout(
      helpers.screenshot({ nodeId, scale }),
      LIMITS.screenshotTimeout,
      () => createError(ERRORS.TIMEOUT, `The screenshot did not finish within ${Math.round(LIMITS.screenshotTimeout / 1000)}s`, {
        hint: 'Bring the builder tab to the front and ask again',
      }),
    )

    if (!shot) {
      throw createError(ERRORS.NOT_READY, `"${refs.pathOf(nodeId)}" is not rendered yet`, { id: nodeId })
    }

    return { ...shot, path: refs.pathOf(nodeId) }
  }

  const handlers = {
    async addNode(op, ctx) {
      const block = requireBlock(op.block)
      const { definition } = block
      const parentId = refs.resolve(op.parent ?? 'ROOT')
      requireCanvas(parentId)
      validateProps(definition, op.props || {}, op.block)
      expandProps(definition, op.props || {}).forEach(([path, value]) => validateValue(definition, path, normalizeValue(path, value), op.block))

      const writes = expandProps(definition, op.props || {}).map(([path, value]) => ({ path, value }))
      const keyframes = requireKeyframes(writes)
      const properties = mergeBlockProps(definition, op.variant, op.props, op.block)
      const custom = { ...op.custom || {}, name: op.name || block.palette?.text || definition?.name || op.block }
      const node = freshNode(block, properties, custom)
      const index = clampIndex(op.index, nodeData(parentId).nodes.length)

      ctx.run().addNodeTree({ rootNodeId: node.id, nodes: { [node.id]: node } }, parentId, index)
      ctx.sync()
      ctx.touch(node.id)
      alias(op.as, node.id)
      await registerSideEffects(ctx, { writes, keyframes }, [
        ...imageEntries(op.block, 'attributes', properties.attributes),
        ...imageEntries(op.block, 'background.image', properties.background?.image),
      ])
      await nextFrame()

      return { id: node.id, linkedNodes: Object.keys(nodeData(node.id)?.linkedNodes || {}) }
    },

    async insertSavedBlock(op, ctx) {
      if (!helpers.fetchSavedBlock) {
        throw createError(ERRORS.INTERNAL, 'Saved blocks are not available here')
      }

      const parentId = refs.resolve(op.parent ?? 'ROOT')
      requireCanvas(parentId)

      const saved = await helpers.fetchSavedBlock(op.blockId)
      if (!saved?.definition) {
        throw createError(ERRORS.SAVED_BLOCK_NOT_FOUND, `Saved block ${op.blockId} was not found`, { blockId: op.blockId })
      }

      const wrapper = requireBlock('template-block')
      const node = freshNode(wrapper, null, { name: saved.name || 'Template Block', blockId: op.blockId, blockVersion: saved.version })
      const index = clampIndex(op.index, nodeData(parentId).nodes.length)

      ctx.run().addNodeTree({ rootNodeId: node.id, nodes: { [node.id]: node } }, parentId, index)
      ctx.sync()
      ctx.run().addNodeTree(parseSerializedTree(query.parseSerializedNode, saved.definition), node.id, 0)
      ctx.touch(node.id)
      alias(op.as, node.id)
      await nextFrame()

      return { id: node.id, children: [...nodeData(node.id)?.nodes || []] }
    },

    async saveBlock(op) {
      if (!helpers.saveBlock) {
        throw createError(ERRORS.INTERNAL, 'Saving blocks is not available here')
      }

      if (!op.name) {
        throw createError(ERRORS.INVALID_OP, 'saveBlock needs a name')
      }

      if (!op.description || !String(op.description).trim()) {
        throw createError(ERRORS.INVALID_OP, 'saveBlock needs a description: what the block is for and when to use it')
      }

      const id = refs.resolve(op.node)
      const block = nodeData(id).name
      const definition = definitionOf(block)
      const level = op.level || 'site'

      if (definition && !definition.canSave) {
        throw createError(ERRORS.INVALID_OP, `"${block}" cannot be saved as a block`, { id, block })
      }

      if (level === 'organization') {
        const siteLevel = [...new Set(subtreeBlocks(id).filter(name => definitionOf(name)?.level === 'site'))]
        if (siteLevel.length) {
          throw createError(ERRORS.INVALID_OP, 'Incompatible level: the subtree contains site-level blocks, save it at the site level', { id, level, blocks: siteLevel })
        }
      }

      return helpers.saveBlock({
        nodeId: id,
        name: String(op.name),
        description: String(op.description).trim(),
        level,
        tags: Array.isArray(op.tags) ? op.tags : [],
      })
    },

    async setProps(op, ctx) {
      const ids = refs.targets(op)
      const device = op.device || 'desktop'
      const merge = op.merge !== false
      requireDevice(device)

      for (const id of ids) {
        requireAllowed(id)
        const block = nodeData(id).name
        const definition = definitionOf(block)
        const staged = stageWrites(definition, block, op.props, { device, merge })

        ctx.run().setProp(id, props => {
          props.properties = props.properties || {}
          applyWrites(props.properties, staged.writes, { device, state: op.state, merge }, (path, message) => ctx.warn({ node: id, path, message }))
        })
        ctx.sync()
        ctx.touch(id)
        await registerSideEffects(ctx, staged, staged.writes.flatMap(({ path, value }) => imageEntries(block, path, value)))
      }

      return { nodes: ids }
    },

    async setPattern(op, ctx) {
      const key = String(op.pattern || '')
      const device = op.device || 'desktop'
      const merge = op.merge !== false

      if (!SHARED_KEYS.includes(key)) {
        throw createError(ERRORS.UNKNOWN_PATTERN, `Unknown style pattern "${key}"`, { pattern: key, known: SHARED_KEYS })
      }

      requireDevice(device)

      const owner = blocks.find(block => block.definition?.isShared && (block.definition.props || []).some(item => item.type === key))
      const definition = owner?.definition
      const block = owner?.key || key
      const staged = stageWrites(definition, block, op.props, { device, merge })

      ctx.run().setCustom('ROOT', custom => {
        custom.stylePatterns = custom.stylePatterns || {}
        if (!custom.stylePatterns[key]) {
          custom.stylePatterns[key] = { ...definition?.defaultProps || {}, opts: SHARED_PATTERN_DEFS[key]?.opts }
        }
        applyWrites(custom.stylePatterns[key], staged.writes, { device, state: op.state, merge }, (path, message) => ctx.warn({ pattern: key, path, message }))
      })
      ctx.sync()
      ctx.touch('ROOT')
      await registerSideEffects(ctx, staged)

      return { pattern: key, selector: patternSelector(key), block: owner?.key || null }
    },

    setCustom(op, ctx) {
      if (!isPlainObject(op.custom)) {
        throw createError(ERRORS.INVALID_OP, 'custom must be an object')
      }

      const ids = refs.targets(op)
      for (const id of ids) {
        requireAllowed(id)
        ctx.run().setCustom(id, custom => {
          Object.assign(custom, op.custom)
        })
        ctx.touch(id)
      }

      return { nodes: ids }
    },

    removeNode(op, ctx) {
      const ids = refs.targets(op)

      for (const id of ids) {
        if (!query.node(id).isDeletable()) {
          throw createError(ERRORS.NOT_DELETABLE, `"${refs.pathOf(id)}" cannot be deleted`, { id, block: nodeData(id)?.name })
        }

        const parentId = nodeData(id)?.parent
        ctx.run().delete(id)
        refs.forgetNode(id)
        ctx.touch(parentId)
      }

      return { removed: ids }
    },

    moveNode(op, ctx) {
      const id = refs.resolve(op.node)
      const parentId = refs.resolve(op.parent)
      const errors = []

      if (!query.node(parentId).isDroppable([id], error => errors.push(error))) {
        throw createError(ERRORS.MOVE_REJECTED, errors[0] || 'Move rejected', { id, parent: parentId })
      }

      const oldParent = nodeData(id).parent
      const oldIndex = parentIndex(id)
      const siblings = nodeData(parentId).nodes.length
      let index = clampIndex(op.index, oldParent === parentId ? siblings - 1 : siblings)

      if (oldParent === parentId && index > oldIndex) {
        index += 1
      }

      ctx.run().move(id, parentId, index)
      ctx.touch(id)
      ctx.touch(parentId)
      ctx.touch(oldParent)

      return { id, parent: parentId }
    },

    async duplicateNode(op, ctx) {
      const id = refs.resolve(op.node)
      requireAllowed(id)
      const data = nodeData(id)

      if (!data.custom?.canDuplicate) {
        throw createError(ERRORS.NOT_DUPLICABLE, `"${refs.pathOf(id)}" cannot be duplicated`, { id, block: data.name })
      }

      const parentId = data.parent
      const tree = cloneNodeTree(query.node, query.parseSerializedNode, id)
      const index = op.index === undefined || op.index === null ? parentIndex(id) + 1 : clampIndex(op.index, nodeData(parentId).nodes.length)

      ctx.run().addNodeTree(tree, parentId, index)
      ctx.touch(tree.rootNodeId)
      alias(op.as, tree.rootNodeId)
      await nextFrame()

      return { id: tree.rootNodeId }
    },

    renameNode(op, ctx) {
      if (!op.name) {
        throw createError(ERRORS.INVALID_OP, 'renameNode needs a name')
      }

      const id = refs.resolve(op.node)
      ctx.run().setCustom(id, custom => {
        custom.name = String(op.name)
      })
      ctx.touch(id)

      return { id }
    },

    setHidden(op, ctx) {
      const ids = refs.targets(op)
      for (const id of ids) {
        ctx.run().setHidden(id, Boolean(op.hidden))
        ctx.touch(id)
      }

      return { nodes: ids }
    },

    selectNode(op) {
      const id = op.node === null || op.node === undefined ? undefined : refs.resolve(op.node)
      actions.selectNode(id)
      return { id: id || null }
    },

    undo() {
      if (!query.history.canUndo()) {
        throw createError(ERRORS.NOTHING_TO_UNDO, 'Nothing to undo')
      }
      actions.history.undo()
      return { undone: true }
    },

    redo() {
      if (!query.history.canRedo()) {
        throw createError(ERRORS.NOTHING_TO_REDO, 'Nothing to redo')
      }
      actions.history.redo()
      return { redone: true }
    },

    outline(op = {}) {
      return buildOutline(currentDocument(), {
        root: resolveRoot(op.root),
        depth: op.depth,
        maxNodes: op.maxNodes ? clamp(Number(op.maxNodes), 1, LIMITS.outlineMaxNodesLimit) : undefined,
      })
    },

    getNode(op) {
      const id = refs.resolve(op.node)
      const [doc] = buildDocument(query.getSerializedNodes(), { root: id }).nodes

      if (!doc) {
        throw createError(ERRORS.NODE_NOT_FOUND, `Node "${id}" not found`, { id })
      }

      doc.path = refs.pathOf(id)
      return op.full ? doc : omit(doc, ['props'])
    },

    async state(op = {}) {
      const include = Array.isArray(op.include) && op.include.length ? op.include : ['document']
      const unknown = include.filter(item => !STATE_INCLUDES.includes(item))

      if (unknown.length) {
        throw createError(ERRORS.INVALID_OP, `Unknown state include: ${unknown.join(', ')}`, { includes: STATE_INCLUDES })
      }

      const root = resolveRoot(op.root)
      const maxChars = clamp(Number(op.maxChars) || LIMITS.stateMaxChars, 1000, LIMITS.stateMaxCharsLimit)
      const result = { revision: revision.current(), root, path: refs.pathOf(root) }

      if (include.includes('document')) {
        result.document = currentDocument(root)
      }

      if (include.includes('html')) {
        result.html = helpers.getHtml ? await helpers.getHtml(root) : null
      }

      if (include.includes('css')) {
        result.css = helpers.getCss ? await helpers.getCss() : null
      }

      if (include.includes('theme')) {
        const custom = rootCustom()
        result.theme = {
          tailwind: custom.tailwind || {},
          colors: custom.colors || [],
          utilities: custom.utilities || [],
          keyframes: knownKeyframes(),
          images: custom.images || [],
        }
      }

      if (include.includes('patterns')) {
        const patterns = rootCustom().stylePatterns || {}
        result.patterns = Object.fromEntries(Object.entries(patterns).map(([key, pattern]) => {
          const { parsedClassName = '', opts: _opts, ...props } = pattern || {}
          return [key, { selector: patternSelector(key), shared: SHARED_KEYS.includes(key), classes: parsedClassName, props }]
        }))
      }

      return truncateState(result, maxChars)
    },

    changes(op = {}) {
      if (!op.since) {
        throw createError(ERRORS.INVALID_OP, 'changes needs "since"')
      }

      const result = revision.diffBetween(op.since)
      if (result.unknownBase) {
        return result
      }

      const root = op.root ? refs.resolve(op.root) : focusRoot()
      if (root === 'ROOT') {
        return result
      }

      const inside = id => refs.exists(id) && refs.isDescendant(id, root)
      const { changes } = result

      return {
        ...result,
        root,
        changes: {
          added: changes.added.filter(doc => inside(doc.id)),
          removed: changes.removed,
          moved: changes.moved.filter(item => inside(item.id)),
          changed: changes.changed.filter(item => inside(item.id)),
        },
      }
    },

    screenshot(op = {}) {
      return takeScreenshot(op)
    },

    async save() {
      if (!helpers.save) {
        throw createError(ERRORS.INTERNAL, 'Saving is not available here')
      }

      try {
        await helpers.waitForFlush?.()
        await helpers.save()
      } catch (error) {
        throw createError(ERRORS.SAVE_FAILED, error?.message || 'Save failed')
      }

      return { saved: true }
    },
  }

  const failure = (error, base, extra = {}) => ({
    ...base,
    ...extra,
    ok: false,
    error: serializeError(error),
    result: { applied: 0, nodes: Object.fromEntries(aliases), data: {} },
  })

  const baseResult = started => ({
    ok: false,
    revision: { before: started.id, after: started.id, hash: started.hash, epoch: revision.epoch },
  })

  const createContext = () => {
    const ctx = {
      touched: new Set(),
      data: {},
      warnings: [],
      warn: warning => ctx.warnings.push(warning),
      pointerStart: store.history.pointer,
      entryCreated: false,
      run: () => ctx.entryCreated ? actions.history.merge() : actions,
      sync: () => {
        ctx.entryCreated = store.history.pointer > ctx.pointerStart
      },
      touch: id => {
        if (id) {
          ctx.touched.add(id)
        }
      },
    }
    return ctx
  }

  const runBatch = async (batch, ctx) => {
    let applied = 0
    let error = null
    let failedOp = null

    for (const [index, op] of batch.ops.entries()) {
      try {
        const data = await runWithLinkedRetry(() => handlers[op.op](op, ctx))
        if (data !== undefined) {
          ctx.data[index] = data
        }
        applied++
        if (MUTATING_OPS.includes(op.op)) {
          ctx.sync()
        }
      } catch (caught) {
        error = { ...serializeError(caught), opIndex: index, op }
        failedOp = op
        break
      }
    }

    let rolledBack = false
    if (error && ctx.entryCreated && !FINAL_OPS.includes(failedOp.op)) {
      actions.history.undo()
      rolledBack = true
    }

    return { applied, error, rolledBack }
  }

  const captureRevision = async () => {
    await nextFrame()
    await settled(store)
    revision.capture()
    return revision.current()
  }

  async function execute(command = {}) {
    const batches = toBatches(command)
    const started = revision.current()
    const base = baseResult(started)
    const fail = (error, extra = {}) => failure(error, base, extra)

    try {
      validateBatches(batches)
    } catch (error) {
      return fail(error)
    }

    if (helpers.isReady && !helpers.isReady()) {
      return fail(createError(ERRORS.NOT_READY, 'The builder is still loading'))
    }

    if (command.expectedRevision && command.expectedRevision !== started.id) {
      const externalChanges = revision.diffBetween(command.expectedRevision)

      if (!command.force) {
        return fail(createError(ERRORS.REVISION_MISMATCH, `The page changed since revision ${command.expectedRevision}`, {
          expected: command.expectedRevision,
          current: started.id,
        }), { externalChanges })
      }

      base.externalChanges = externalChanges
    }

    const dependencies = batchDependencies(batches)
    const outcomes = new Map()
    const results = []
    const touched = new Set()
    let batchStart = started

    for (const [batchIndex, batch] of batches.entries()) {
      const blocked = dependencies[batchIndex].filter(name => outcomes.get(name) !== 'done')

      if (blocked.length) {
        outcomes.set(batch.name, 'skipped')
        results.push({ name: batch.name, status: 'skipped', applied: 0, skippedBecause: blocked })
        continue
      }

      const ctx = createContext()
      const before = new Set(aliases.keys())
      const { applied, error, rolledBack } = await runBatch(batch, ctx)
      const status = error ? 'failed' : 'done'
      const entry = {
        name: batch.name,
        status,
        applied,
        nodes: Object.fromEntries([...aliases].filter(([name]) => !before.has(name))),
        data: ctx.data,
      }

      if (ctx.warnings.length) {
        entry.warnings = ctx.warnings
      }

      if (batches.length > 1) {
        const after = await captureRevision()
        const between = revision.diffBetween(batchStart.id, after.id)
        entry.revision = after.id
        entry.diff = between.unknownBase ? between : between.changes
        batchStart = after
      }

      if (error) {
        entry.error = error
        entry.rolledBack = rolledBack
      } else {
        ctx.touched.forEach(id => touched.add(id))
      }

      outcomes.set(batch.name, status)
      results.push(entry)
    }

    const after = batches.length > 1 ? revision.current() : await captureRevision()
    const between = revision.diffBetween(started.id, after.id)
    const failed = results.find(entry => entry.status === 'failed')

    const result = {
      ...base,
      ok: results.every(entry => entry.status === 'done'),
      revision: { ...base.revision, after: after.id, hash: after.hash },
      diff: between.unknownBase ? between : between.changes,
      batches: results,
      result: {
        applied: results.reduce((total, entry) => total + entry.applied, 0),
        nodes: Object.fromEntries(aliases),
        data: results[0]?.data || {},
      },
    }

    if (touched.size) {
      result.result.outline = touchedOutline(touched)
    }

    const warnings = results.flatMap(entry => (entry.warnings || []).map(warning => ({ batch: entry.name, ...warning })))
    if (warnings.length) {
      result.warnings = warnings
    }

    if (failed) {
      result.error = failed.error
      if (batches.length === 1) {
        result.rolledBack = failed.rolledBack
      }
    }

    if (command.screenshot) {
      try {
        result.screenshot = await takeScreenshot(isPlainObject(command.screenshot) ? command.screenshot : {}, touched)
      } catch (error) {
        result.screenshot = { error: serializeError(error) }
      }
    }

    return result
  }

  const applyOps = ops => execute({ id: localCommandId(), ops })
  const applyBatches = batches => execute({ id: localCommandId(), batches })

  return {
    execute,
    failure: error => failure(error, baseResult(revision.current())),
    applyOps,
    applyBatches,
    outline: options => handlers.outline(options || {}),
    state: options => handlers.state(options || {}),
    changes: since => handlers.changes(typeof since === 'string' ? { since } : since || {}),
    getNode: (node, full = false) => handlers.getNode({ node, full }),
    screenshot: options => takeScreenshot(options || {}),
    setFocus: ref => {
      focus = ref ? String(ref) : null
      return focusRoot()
    },
    isReady: () => !helpers.isReady || helpers.isReady(),
    aliases,
    revision,
    dispose: () => revision.stop(),
  }
}
